import { Card } from '@/components/ui/card';
import { ThemeKey, useAppTheme } from '@/contexts/ThemeContext';
import { THEME_METADATA } from '@/lib/themes';
import { THEME_PALETTES } from '@/lib/theme-palettes';
import { Check } from 'lucide-react';

interface ThemePreviewProps {
  themeKey: ThemeKey;
  isSelected: boolean;
  onSelect: (themeKey: ThemeKey) => void;
}

export function ThemePreview({ themeKey, isSelected, onSelect }: ThemePreviewProps) {
  const { themeMode } = useAppTheme();
  const metadata = THEME_METADATA[themeKey];
  const colors = THEME_PALETTES[themeKey][themeMode];

  return (
    <Card 
      className="relative p-0 cursor-pointer transition-all hover:shadow-lg overflow-hidden group"
      onClick={() => onSelect(themeKey)}
      style={{ 
        backgroundColor: colors.surface,
        borderColor: isSelected ? colors.primary : colors.border,
        borderWidth: isSelected ? '2px' : '1px'
      }}
    >
      {/* Racing stripe for Coast Guard and Marines */}
      {colors.headerStripe && (
        <div 
          className="h-1 w-full"
          style={{ backgroundColor: colors.headerStripe }}
        />
      )}
      
      {/* Orange header bar for Space Force */}
      {themeKey === 'spaceforce' && (
        <div 
          className="h-2 w-full"
          style={{ 
            backgroundColor: themeMode === 'dark' ? colors.primary : colors.primary 
          }}
        />
      )}
      
      {isSelected && (
        <div 
          className="absolute top-2 right-2 rounded-full p-1 z-10"
          style={{ 
            backgroundColor: colors.primary, 
            color: colors.primaryText 
          }}
        >
          <Check className="h-4 w-4" />
        </div>
      )}
      
      <div className="flex h-full">
        {/* Sidebar Preview Strip */}
        <div 
          className="w-12 flex-shrink-0 transition-all group-hover:w-14"
          style={{ backgroundColor: colors.navBackground }}
        >
          <div className="h-full flex flex-col items-center justify-center gap-2 py-4">
            {/* Active nav item */}
            <div 
              className="w-7 h-7 rounded transition-all"
              style={{ 
                backgroundColor: colors.navActive,
                boxShadow: `0 0 0 2px ${colors.focusRing}40`
              }}
            />
            {/* Hover state nav item */}
            <div 
              className="w-7 h-7 rounded transition-all"
              style={{ backgroundColor: colors.navHover }}
            />
            {/* Default state nav item */}
            <div 
              className="w-7 h-7 rounded transition-all opacity-60"
              style={{ backgroundColor: colors.navDefault }}
            />
          </div>
        </div>
        
        {/* Main Content Area */}
        <div className="flex-1 p-4 space-y-3">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h3 
                className="font-semibold text-base"
                style={{ color: colors.textPrimary }}
              >
                {metadata.name}
              </h3>
              <p 
                className="text-xs mt-0.5"
                style={{ color: colors.textSecondary }}
              >
                {metadata.description}
              </p>
            </div>
          </div>
          
          {/* Card Header Preview */}
          <div 
            className="rounded-t-lg px-3 py-2 flex items-center justify-between"
            style={{ 
              backgroundColor: colors.cardHeaderBackground,
            }}
          >
            <span 
              className="text-xs font-semibold"
              style={{ color: colors.cardHeaderText }}
            >
              Mission Status
            </span>
            <div 
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: colors.accent }}
            />
          </div>
          
          {/* Mini Dashboard Preview */}
          <div 
            className="rounded-b-lg p-3 space-y-2.5 border border-t-0"
            style={{ 
              backgroundColor: colors.surfaceAlt,
              borderColor: colors.border
            }}
          >
            {/* Progress bar with proper contrast */}
            <div className="space-y-1">
              <div className="flex justify-between items-center">
                <span 
                  className="text-xs"
                  style={{ color: colors.textSecondary }}
                >
                  Readiness
                </span>
                <span 
                  className="text-xs font-medium"
                  style={{ color: colors.textPrimary }}
                >
                  68%
                </span>
              </div>
              <div 
                className="h-2 rounded-full overflow-hidden"
                style={{ backgroundColor: colors.progressTrack }}
              >
                <div 
                  className="h-full rounded-full transition-all"
                  style={{ 
                    backgroundColor: colors.progressFill,
                    width: '68%'
                  }}
                />
              </div>
            </div>
            
          {/* Buttons - Navy gets special outline style */}
            <div className="flex gap-2 pt-1">
              {themeKey === 'navy' ? (
                <>
                  <div 
                    className="flex-1 h-7 rounded-md flex items-center justify-center text-xs font-medium transition-all"
                    style={{ 
                      backgroundColor: 'transparent',
                      border: `2px solid ${colors.primary}`,
                      color: colors.primary
                    }}
                  >
                    Primary
                  </div>
                  <div 
                    className="flex-1 h-7 rounded-md flex items-center justify-center text-xs font-medium transition-all"
                    style={{ 
                      backgroundColor: colors.secondary,
                      color: colors.secondaryText
                    }}
                  >
                    Secondary
                  </div>
                </>
              ) : (
                <>
                  <div 
                    className="flex-1 h-7 rounded flex items-center justify-center text-xs font-medium transition-all"
                    style={{ 
                      backgroundColor: colors.primary,
                      color: colors.primaryText
                    }}
                  >
                    Primary
                  </div>
                  <div 
                    className="flex-1 h-7 rounded flex items-center justify-center text-xs font-medium transition-all"
                    style={{ 
                      backgroundColor: colors.secondary,
                      color: colors.secondaryText
                    }}
                  >
                    Secondary
                  </div>
                </>
              )}
            </div>
            
            {/* Input preview - Navy gets special underline style */}
            {themeKey === 'navy' && (
              <div className="pt-1">
                <div 
                  className="h-6 flex items-end"
                  style={{ 
                    borderBottom: `2px solid ${colors.inputFocusBorder}`,
                    color: colors.textSecondary,
                    fontSize: '10px'
                  }}
                >
                  <span>Input field</span>
                </div>
              </div>
            )}
            
            {/* Badges */}
            <div className="flex gap-2 pt-1">
              <span 
                className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold transition-all"
                style={{ 
                  backgroundColor: colors.badgePrimary,
                  color: colors.badgePrimaryText
                }}
              >
                In Progress
              </span>
              <span 
                className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold transition-all"
                style={{ 
                  backgroundColor: colors.badgeSecondary,
                  color: colors.badgeSecondaryText
                }}
              >
                7 Skills
              </span>
            </div>
          </div>
          
          {/* Inspiration text */}
          <p 
            className="text-xs italic pt-1"
            style={{ color: colors.textSecondary }}
          >
            {metadata.inspiration}
          </p>
        </div>
      </div>
    </Card>
  );
}
